// @flow

import { getApiBaseUrlByTenantCode, type TenantCode } from '@tectonics/commons/tenant';
import type { RiskSignals } from '../hooks/use-risk-signals';
import type { RegistrationOptions, CredentialResponse } from './webauthn';

type EnrollmentInitiationPayload = {|
  +contract: string,
  +payment_type_code: string,
  +operation: string,
  +enrollment: {|
    +country: string,
    +notification_url: string,
    +identification_type: string,
    +identification: string,
    +account_details: {|
      +bank_id: string,
    |},
    +fidoOptions: {|
      +platform: string,
      +risk_signals: RiskSignals,
    |},
  |},
|};

type BankListItem = {|
  +code: string,
  +name: string,
  +logo_url?: string,
|};

type EnrollmentInitiationResponse = {|
  +redirect_url: string,
|};

type EnrollmentStatusResponse = {|
  +status: string,
  +enrollment?: {|
    +enrollment_code?: string,
    +status?: string,
    +bank_id?: string,
    +identification?: string,
    +registration_options?: RegistrationOptions,
    +intent_url?: string,
  |},
|};

type EnrollmentAuthPayload = {|
  +contract: string,
  +payment_type_code: string,
  +operation: string,
  +enrollment: {|
    +authentication: CredentialResponse,
  |},
|};

type EnrollmentAuthResponse = {|
  +status: string,
  +payment_type?: string,
  +error?: string,
  +message?: string,
  +enrollment?: {|
    +status?: string,
  |},
|};

function getBaseUrl(tenantCode: TenantCode): string {
  return getApiBaseUrlByTenantCode(tenantCode);
}

export async function fetchBankList(
  contract: string,
  tenantCode: TenantCode,
): Promise<Array<BankListItem>> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment/bank-list?contract=${encodeURIComponent(contract)}&country_code=br&payment_type=pix-jsr`,
  );

  const data = await response.json();

  if (!response.ok || !Array.isArray(data)) {
    throw new Error(data.error || 'Falha ao carregar lista de bancos.');
  }

  return data;
}

export async function initiateEnrollment(
  payload: EnrollmentInitiationPayload,
  tenantCode: TenantCode,
): Promise<EnrollmentInitiationResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );

  const data = await response.json();

  if (!response.ok || !data.redirect_url) {
    throw new Error(data.error || 'Falha ao iniciar vincula\u00E7\u00E3o.');
  }

  return data;
}

export async function pollEnrollmentStatus(
  contract: string,
  tenantCode: TenantCode,
): Promise<EnrollmentStatusResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment/query?contract=${encodeURIComponent(contract)}`,
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Falha ao verificar status.');
  }

  return data;
}

export async function confirmEnrollment(
  payload: EnrollmentAuthPayload,
  tenantCode: TenantCode,
): Promise<EnrollmentAuthResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment/authentication`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Falha ao confirmar vincula\u00E7\u00E3o.');
  }

  return data;
}
